import { isPast } from "date-fns/isPast";
import { isFuture } from "date-fns/isFuture";
import { isToday } from "date-fns/isToday";
import { addMinutes } from "date-fns/addMinutes";
import { subMinutes } from "date-fns/subMinutes";
import { parseISO } from "date-fns/parseISO";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import isNumber from "lodash/isNumber";

import { createOddsElement } from "./odds";

import "./sportnyhet-data.scss";

const query = (selector = "", parent = document) => {
	return parent.querySelector(selector);
};
const queryAll = (selector = "", parent = document) => {
	return Array.from(parent.querySelectorAll(selector));
};

const getCurrentTime = () => {
	const instance = new Date();
	return instance.getTime();
};

const getMatches = () =>
	queryAll(".sn-scores").map((el) => {
		const {
			dataset: { id, startTime },
		} = el;

		return {
			id,
			startTime,
			startsAt: parseISO(startTime),
			hasLiveOdds: !!query(".sn-live-odds", el.parentNode.parentNode),
		};
	});
const isMatchPlaying = ({ startsAt, hasLiveOdds }) =>
	(isToday(startsAt) && isPast(startsAt)) || hasLiveOdds;
const getPlayingMatches = () => getMatches().filter(isMatchPlaying);
const shouldUpdateMatches = () => getPlayingMatches().length;

const updateMatches = (data) => {
	const matches = data.flat();
	if (!matches.length) {
		return;
	}

	queryAll(".sn-scores").forEach((el) => {
		const { sport } = el.dataset;

		const match = matches.find(({ unique }) => unique === el.dataset.matchId);

		const parentElement = el.parentNode.parentNode;
		const oddsElement = query(".sn-live-odds", parentElement);

		if (oddsElement && !match) {
			hideMatch(parentElement);
		}

		if (!match) {
			return;
		}

		const {
			status,
			timer,
			period,
			datetime,
			home: { totalscore: homeScore },
			away: { totalscore: awayScore },
			live: { odds },
		} = match;

		let startsAt = null;

		if (datetime) {
			startsAt = parseISO(datetime);
		}

		if ((!status || status === "not-started") && !oddsElement) {
			return;
		}

		const timerElement = query(".sn-timer", el.parentNode);
		const periodElement = query(".sn-period", el.parentNode);

		const { label: periodLabel, break: breakLabel } = periodElement.dataset;
		const overTimeLabel = periodElement.dataset.overTime;

		let isPlaying = false;

		if (timerElement) {
			if (
				["break-time", "et", "wo", "int", "half-time", "ht"].includes(status)
			) {
				timerElement.innerText = "";
				timerElement.classList.add("sn-timer-active");
				if (periodElement) {
					periodElement.innerText = breakLabel || "";
				}
				isPlaying = true;
			} else if (status === "over-time") {
				timerElement.innerText = "";
				timerElement.classList.add("sn-timer-active");
				if (periodElement) {
					periodElement.innerText = overTimeLabel || "";
				}
				isPlaying = true;
			} else if (
				status !== "not-started" &&
				(isNumber(status) ||
					timer > 0 ||
					/^\d+$/.test(status) ||
					["1st-half", "2nd-half"].includes(status))
			) {
				timerElement.innerText = timer;
				timerElement.classList.add("sn-timer-active");
				if (periodElement) {
					const str =
						period && isNumber(period) ? `${periodLabel} ${period}` : period;
					periodElement.innerText = period ? str : "";
				}
				el.classList.add("sn-scores-active");
				isPlaying = true;
			} else {
				timerElement.innerText = "";
				timerElement.classList.remove("sn-timer-active");
				if (periodElement) {
					periodElement.innerText = "";
				}
			}
			if (period !== undefined) {
				timerElement.classList[period ? "add" : "remove"]("sn-timer-active");

				if (period) {
					isPlaying = true;
				}
			}
		}

		if ((isPlaying && oddsElement) || !oddsElement) {
			showMatch(parentElement);
		}

		query(".sn-score--home", el).innerText = homeScore;
		query(".sn-score--away", el).innerText = awayScore;

		if (oddsElement) {
			const { url, label, types } = oddsElement.dataset;
			oddsElement.innerHTML = "";

			if (isPlaying && Array.isArray(odds)) {
				const oddsHTML = createOddsElement({
					sport,
					odds,
					url,
					label,
					types: JSON.parse(types),
				});

				if (oddsHTML) {
					oddsElement.innerHTML = oddsHTML;
				}
			}

			if (!isPlaying) {
				let hide = true;

				if (startsAt !== null) {
					const diff = differenceInMinutes(startsAt, new Date());

					if (diff < 60) {
						hide = false;
					}

					if (diff < -170) {
						hide = true;
					}
				}

				if (hide) {
					hideMatch(parentElement);
				}
			}
		}
	});

	setTimeout(() => {
		fetchData().then(updateMatches).catch(console.error);
	}, 30000);
};

const hideMatch = (parentElement) => {
	parentElement.style.display = "none";

	if (parentElement.parentNode) {
		let foundVisible = false;
		queryAll(".sn-row", parentElement.parentNode)
			.filter((el) => !el.classList.contains("sn-row--head"))
			.forEach((el) => {
				if (el.style.display !== "none") {
					foundVisible = true;
				}
			});
		if (!foundVisible) {
			parentElement.parentNode.style.display = "none";
		}
	}
};

const showMatch = (parentElement) => {
	parentElement.removeAttribute("style");

	if (parentElement.parentNode) {
		parentElement.parentNode.removeAttribute("style");
	}
};

const fetchData = () => {
	const ts = getCurrentTime();

	return Promise.all(
		Array.from(
			new Set(
				queryAll(".sn-scores")
					.map(({ dataset: { sport, leagueId, country } }) => ({
						sport,
						leagueId,
						country,
					}))
					.map(
						({ sport, leagueId, country }) =>
							`/wp-json/sportnyhet/v3/sports/${
								sport || "hockey"
							}/sources/livescores/countries/${country}/leagues/${leagueId}/items?ts=${ts}`
					)
			)
		).map((url) =>
			fetch(url, { method: "POST" }).then((response) => {
				if (!response.ok) {
					throw new Error("Endpoint error");
				}
				return response.json();
			})
		)
	);
};

const init = () =>
	new Promise((resolve, reject) => {
		if (shouldUpdateMatches()) {
			return resolve();
		}

		const i = setInterval(() => {
			if (shouldUpdateMatches()) {
				clearInterval(i);
				resolve();
			}
		}, 10000);
	});

init().then(fetchData).then(updateMatches).catch(console.error);

const switcher = query(".sn-switcher");
if (switcher) {
	window.addEventListener("click", ({ target }) => {
		if (!switcher.contains(target)) {
			switcher.classList.remove("sn-switcher--active");
		}
	});
}

queryAll(".sn-switch").forEach((el) => {
	el.addEventListener("click", () => {
		const parent = el.parentNode.parentNode;
		parent.classList.toggle(
			"sn-switcher--active",
			el.getAttribute("aria-selected") === "true"
		);
		queryAll(".sn-switch", parent).forEach((item) => {
			item.setAttribute("aria-selected", item === el ? "true" : "false");
			const panelId = item.getAttribute("aria-controls");
			const panel = query(`#${panelId}`);
			if (panel) {
				panel.style.display = item === el ? "block" : "none";
			}
		});
	});
});

(function () {
	const showOddsLiveBtnTables = queryAll(".sn-table--show-live-odds-btn");

	const updateOddsLiveBtn = () =>
		showOddsLiveBtnTables.forEach((tbl) => {
			const sport = tbl.dataset?.sport || null;
			let approxEnd = 150; // Hockey

			switch (sport) {
				case "handball":
					approxEnd = 60;
					break;
				case "soccer":
					approxEnd = 90;
					break;
			}

			const isLivescores = !!tbl.className.match(/sn-table--livescores/);

			const label = tbl.dataset?.oddslivebtnLabel;
			const link = tbl.dataset?.oddslivebtnLink;

			if (!label || !link) {
				// No label or link. Skip.
				return;
			}

			queryAll(".sn-row", tbl).forEach((row) => {
				const time = query(".sn-cell--time time", row);
				const status = row.dataset?.status || null;

				if (!time) {
					// No time element. Skip.
					return;
				}

				const dateTime = time.getAttribute("datetime");

				if (!dateTime) {
					// No datetime. Skip.
					return;
				}

				const dt = parseISO(dateTime);

				let isPlaying = false;

				if (
					!status ||
					![
						"ft",
						"finished",
						"after-penalties",
						"pen",
						"after-over-time",
						"aet",
					].includes(status)
				) {
					isPlaying =
						isPast(subMinutes(dt, 10)) && isFuture(addMinutes(dt, approxEnd));
				}

				const btn = query(".sn-oddslivebtn", row);

				const cells = queryAll(".sn-cell", row);

				// const rowText = cells[0].innerText.trim() + ' ' + query('.sn-bold', cells[1]).innerText.trim()

				if (btn) {
					if (isPlaying) {
						// console.log('keep', rowText);
						return;
					}
					// console.log('remove', rowText);
					btn.remove();
					return;
				}

				if (!isPlaying) {
					return;
				}

				if (cells.length < 2) {
					return;
				}

				// console.log('create', rowText);

				const newBtn = document.createElement("a");
				newBtn.classList.add("sn-oddslivebtn");
				newBtn.href = link;
				newBtn.innerText = label;
				newBtn.target = "_blank";

				const cellIndex = isLivescores ? 2 : 1;

				setTimeout(() => cells[cellIndex].append(newBtn), 0);
			});
		});

	if (!showOddsLiveBtnTables.length) {
		return;
	}

	setInterval(updateOddsLiveBtn, 30000);

	updateOddsLiveBtn();
})();

const openMatchWindow = (el) => {
	const width = 680;
	const height = 900;

	// const { width: screenWidth, height: screenHeight } = window.screen;

	// var dualScreenLeft =
	// 	window.screenLeft !== undefined ? window.screenLeft : screen.left;
	// var dualScreenTop =
	// 	window.screenTop !== undefined ? window.screenTop : screen.top;

	// const left = Math.round(screenWidth / 2 - width / 2) + dualScreenLeft;
	// const top = Math.round(screenHeight / 2 - height / 2) + dualScreenTop;

	const features = [
		"popup=1",
		"menubar=0",
		"status=0",
		"toolbar=0",
		// `top=${top}`,
		// `left=${left}`,
		// `screenY=${top}`,
		// `screenX=${left}`,
		`width=${width}`,
		`height=${height}`,
		`innerWidth=${width}`,
		`innerHeight=${height}`,
	];

	window.open(el.href, "sportnyhet-match", features.join(","));
};

queryAll("a.sn-row, .sn-row-match-link").forEach((el) => {
	el.addEventListener("click", (event) => {
		event.preventDefault();
		event.stopPropagation();

		openMatchWindow(el);
	});
});
